import {
  CarrierType,
  CarrierVisitDirection,
  ContainerDamageUpsertDto,
  OrderResponseDto,
  OrderStatus,
  StowagePositionDto,
  UpdateOrderCommand,
} from '@planning/app/api'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import { containerService, orderService } from '@planning/services'
import { CancellableTimeoutStore } from '@planning/stores/gateClerk/CancellableTimeoutStore'
import { AxiosError } from 'axios'
import { action, makeObservable, observable, runInAction } from 'mobx'

export class CheckOrderNotificationStore extends CancellableTimeoutStore {
  order?: OrderResponseDto
  isDirectRestow = false

  constructor(delay?: number) {
    super(delay)
    makeObservable(this, {
      order: observable,
      isDirectRestow: observable,
      setOrder: action,
      setIsDirectRestow: action,
    })
  }

  setOrder = (order?: OrderResponseDto) => {
    this.order = order
  }

  setIsDirectRestow = (isDirectRestow: boolean) => {
    this.isDirectRestow = isDirectRestow
  }

  createCheckInRequest = async (
    data: IInspectContainerFormData,
    order?: OrderResponseDto,
    tallymanOperationsUIStore?: any,
    selectedCraneId?: number,
    selectedGangId?: number,
    isRestow?: boolean,
    stowagePositionDto?: StowagePositionDto,
  ) => {
    if (!order) return

    if (order.direction === CarrierVisitDirection.Inbound && !tallymanOperationsUIStore) return

    this.setOrder(order)
    this.setIsDirectRestow(
      order.direction === CarrierVisitDirection.Inbound && stowagePositionDto !== undefined,
    ) // If an inbound checkin has informed stowage position, it means a direct restow

    const selectedEquipmentId = tallymanOperationsUIStore?.selectedEquipmentId
    const selectedContainerPosition = tallymanOperationsUIStore?.selectedContainerPosition

    this.createRequest(async () => {
      try {
        if (order.direction === CarrierVisitDirection.Inbound)
          await tallymanOperationsUIStore?.confirmSelectedEquipment(
            order.carrierVisitId,
            order.id,
            selectedCraneId,
            selectedEquipmentId,
            selectedGangId,
            selectedContainerPosition,
          )

        let damages: ContainerDamageUpsertDto[] = []
        if (data?.containerNumber && data?.damagesReported?.length) {
          damages = await containerService.getDamageDtoForUploadedDamages(
            data.containerNumber,
            data.damagesReported,
          )
        }

        const updatedOrder = {
          ...data,
          id: data?.id ?? order?.id,
          containerDamages: damages,
          isAccidentalDischarge: false,
          isToBeStoredInYard: true,
          isOOG: data?.oog?.isOog,
          overWidthRight: data?.oog?.overWidthRight,
          overWidthLeft: data?.oog?.overWidthLeft,
          overLengthRear: data?.oog?.overLengthRear,
          overLengthFront: data?.oog?.overLengthFront,
          overHeight: data?.oog?.overHeight,
          unloadingMethod: data?.oog?.unloadingMethod,
        } as UpdateOrderCommand

        await orderService.update(updatedOrder)

        if (isRestow) {
          await orderService.allowRestowPassage({
            id: updatedOrder.id,
            carrierVisitDirection: updatedOrder.direction,
            craneId: selectedCraneId,
            stowagePosition: stowagePositionDto,
            isDirectRestow: this.isDirectRestow,
          })
        } else {
          await orderService.allowPassage({
            id: updatedOrder.id,
            carrierVisitDirection: updatedOrder.direction,
            craneId: selectedCraneId,
          })
        }

        if (
          order.carrierVisitType === CarrierType.Vessel &&
          order.direction === CarrierVisitDirection.Outbound &&
          selectedCraneId
        )
          await tallymanOperationsUIStore?.confirmCraneLoadingJob(
            order.id,
            selectedCraneId,
            selectedGangId,
          )

        this.reset()
      } catch (error) {
        // roll back the optimistic order status update
        runInAction(() => {
          this.order!.status = OrderStatus.Open
        })

        const err = error as AxiosError
        this.setRequestErrorMessage(err.message)

        // interim sln for showing error before reset state that will work only the current one
        setTimeout(async () => {
          this.reset()
        }, this.delay)
      }
    })

    // optimistic order status update
    runInAction(() => {
      this.order!.status = OrderStatus.Fulfilled
    })
  }

  cancelRequest = () => {
    super.cancelRequest()

    setTimeout(async () => {
      this.reset()
    }, this.delay)
  }

  reset() {
    super.reset()
    this.setOrder()
  }
}
